import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Locations', href: '/locations' },
];

interface HeaderProps {
  onEstimateClick: () => void;
}

export default function Header({ onEstimateClick }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md border-b border-oak/20 shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <div className="h-14 w-auto flex items-center justify-center">
              <img
                src="https://storage.readdy-site.link/project_files/e9d30abd-d6d8-4b84-ac41-23b773cb6a49/56a91e8e-7d83-4a3d-b7d3-a26defbf722a_premierlogo.png?v=d95b0f6312ed03f71c5c0454ebe11315"
                alt="Premier Hardwood Floors Logo"
                className="h-14 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div
                key={link.href}
                className="relative"
                onMouseEnter={() => link.children && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className={`font-sans text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                    scrolled ? 'text-charcoal hover:text-oak' : 'text-white/90 hover:text-gold'
                  } ${location.pathname === link.href ? (scrolled ? 'text-oak' : 'text-gold') : ''}`}
                >
                  {link.label}
                  {link.children && <i className="ri-arrow-down-s-line text-xs" />}
                </Link>
                {link.children && activeDropdown === link.label && (
                  <div className="absolute top-full left-0 pt-2">
                    <div className="bg-white rounded-xl shadow-lg border border-sand-dark py-2 min-w-52">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-5 py-3 text-sm text-charcoal hover:text-oak hover:bg-sand transition-colors font-sans"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+14105550192"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                scrolled ? 'bg-charcoal text-white hover:bg-walnut' : 'bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30'
              }`}
            >
              <i className="ri-phone-line text-sm" />
              (410) 555-0192
            </a>
            <button
              onClick={onEstimateClick}
              className="flex items-center gap-2 px-5 py-2.5 bg-gold text-walnut rounded-lg text-sm font-semibold hover:bg-gold-dark transition-all whitespace-nowrap"
            >
              Request Estimate
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${scrolled ? 'text-walnut' : 'text-white'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-sand-dark px-6 py-4">
            {navLinks.map((link) => (
              <div key={link.href}>
                <Link
                  to={link.href}
                  className="block py-3 text-base font-medium text-charcoal hover:text-oak border-b border-sand-dark"
                >
                  {link.label}
                </Link>
                {link.children && (
                  <div className="pl-4">
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="block py-2.5 text-sm text-oak hover:text-walnut"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <a href="tel:+14105550192" className="flex items-center justify-center gap-2 py-3 bg-charcoal text-white rounded-lg font-medium">
                <i className="ri-phone-line" /> (410) 555-0192
              </a>
              <button
                onClick={onEstimateClick}
                className="py-3 bg-gold text-walnut rounded-lg font-semibold"
              >
                Request Free Estimate
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Floating Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden flex bg-walnut border-t border-walnut-light">
        <a
          href="tel:+14105550192"
          className="flex-1 flex items-center justify-center gap-2 py-4 text-white font-medium text-sm border-r border-walnut-light"
        >
          <i className="ri-phone-line" /> Call Now
        </a>
        <button
          onClick={onEstimateClick}
          className="flex-1 flex items-center justify-center gap-2 py-4 bg-gold text-walnut font-semibold text-sm"
        >
          <i className="ri-file-list-line" /> Free Estimate
        </button>
      </div>
    </>
  );
}
